import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useForm, Controller } from 'react-hook-form';
import Select from 'react-select';
import menuPortalProps from 'helpers/reactSelectPortal';
import { useDispatch } from 'react-redux';
import { FaTrashAlt } from 'react-icons/fa';
import { getCarsBySite, getDriversBySite } from 'redux/booking/car/carBookingSlice';
import { TwIconButton } from 'components/common';

const TransactionDetailAssignDriverForm = React.memo(({ index, driver, handleRemoveDriver, transaction, updateAssignDriver }) => {
  const { control, formState: { errors }, setValue } = useForm();
  const [drivers, setDrivers] = useState([]);
  const [carOptions, setCarOptions] = useState([]);
  const [driverOptions, setDriverOptions] = useState([]);
  const dispatch = useDispatch();

  const params = {
    from_date: transaction.from_date,
    to_date: transaction.to_date,
    site_id: transaction.site_id,
  };

  useEffect(() => {
    const fetchCarList = async () => {
      try {
        const response = await dispatch(getCarsBySite(params));
        const data = response.payload;
        setCarOptions((data || []).map((item) => ({ value: item.id, label: item.name })));
      } catch (error) {
        console.error(error);
      }
    };

    const fetchDriverList = async () => {
      try {
        const response = await dispatch(getDriversBySite(params));
        const data = response.payload || [];
        setDrivers(data);
        setDriverOptions(data.map((item) => ({ value: item.id, label: item.name })));
      } catch (error) {
        console.error(error);
      }
    };

    fetchCarList();
    fetchDriverList();
    // load options once on open; `params` is rebuilt every render, so depending on it would refetch on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (driver && driver.driver_id) {
      setValue('driver_id', driver.driver_id);
      setValue('car_id', driver.car_id);
    }
  }, [driver, setValue]);

  const handleDriverChange = (value) => {
    const selected = drivers.find((item) => item.id === value);
    if (selected) updateAssignDriver(index, selected);
  };

  return (
    <div className="tw-mb-4">
      <div className="tw-flex tw-justify-end tw-mb-2">
        <TwIconButton variant="secondary" size="sm" icon={FaTrashAlt} onClick={handleRemoveDriver} title="Remove" />
      </div>

      <div className="tw-mb-3">
        <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5">Driver name</label>
        <Controller
          control={control}
          name="driver_id"
          rules={{ required: 'Driver must be required' }}
          render={({ field }) => (
            <Select
              closeMenuOnSelect
              options={driverOptions}
              placeholder="Select..."
              classNamePrefix="react-select" {...menuPortalProps}
              value={driverOptions.find((option) => option.value === field.value) || null}
              onChange={(selectedOption) => handleDriverChange(selectedOption.value)}
            />
          )}
        />
        {errors.driver_id && <p className="tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0">{errors.driver_id.message}</p>}
      </div>

      <div className="tw-pb-4 tw-border-b tw-border-dashed tw-border-slate-200">
        <label className="tw-block tw-text-sm tw-font-medium tw-text-slate-700 tw-mb-1.5">Car name</label>
        <Controller
          control={control}
          name="car_id"
          rules={{ required: { value: true, message: 'Car must be required' } }}
          render={({ field }) => (
            <Select
              closeMenuOnSelect
              options={carOptions}
              placeholder="Select..."
              classNamePrefix="react-select" {...menuPortalProps}
              value={carOptions.find((option) => option.value === field.value) || null}
              onChange={(selectedOption) => setValue('car_id', selectedOption.value)}
            />
          )}
        />
        {errors.car_id && <p className="tw-text-xs tw-text-red-600 tw-mt-1 tw-mb-0">{errors.car_id.message}</p>}
      </div>
    </div>
  );
});

TransactionDetailAssignDriverForm.displayName = 'TransactionDetailAssignDriverForm';

TransactionDetailAssignDriverForm.propTypes = {
  index: PropTypes.number.isRequired,
  driver: PropTypes.object.isRequired,
  handleRemoveDriver: PropTypes.func.isRequired,
  transaction: PropTypes.object.isRequired,
  updateAssignDriver: PropTypes.func.isRequired,
};

export default TransactionDetailAssignDriverForm;
